import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import BottomNav from '../components/BottomNav';
import { trackSelection } from '../utils/analytics';
import { clearPassedOnboarding } from '../utils/userSelections';
import { callCloudFunction, FIREBASE_FUNCTIONS } from '../utils/firebaseFunctions';
import { firebaseApp } from '../utils/firebaseConfig';
import { requestFlutterSignOut } from '../components/useFlutterGoogleAuth';

const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';
const BLUE_ACCENT = '#3B7BF4';

const SettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.language?.startsWith('he') ?? false;

  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleSignOut = useCallback(async () => {
    try {
      const auth = getAuth(firebaseApp);
      await signOut(auth);
    } catch (e) {
      console.warn('Firebase signOut error (continuing with local sign-out):', e);
    }
    await requestFlutterSignOut();
    localStorage.removeItem('loggedInUser');
    localStorage.removeItem('selectedRabbi');
    localStorage.removeItem('rabbiToken');
    localStorage.removeItem('rabbiInfo');
    clearPassedOnboarding();
    navigate('/');
    window.location.reload();
  }, [navigate]);

  const handleDeleteAccountClick = useCallback(() => {
    setDeleteAccountError(null);
    setShowDeleteAccountConfirm(true);
  }, []);

  const handleDeleteAccountConfirm = useCallback(async () => {
    const raw = localStorage.getItem('loggedInUser');
    let userId: string | null = null;
    if (raw) {
      try {
        const u = JSON.parse(raw);
        userId = u?.userId ?? u?.uid ?? null;
      } catch {
        /* ignore */
      }
    }
    if (!userId) {
      setDeleteAccountError(t('delete_account_error_not_logged_in', 'You are not logged in.'));
      return;
    }
    setIsDeletingAccount(true);
    setDeleteAccountError(null);
    try {
      const response = await callCloudFunction(
        'deleteAccount',
        FIREBASE_FUNCTIONS.deleteAccount(),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        },
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setShowDeleteAccountConfirm(false);
        await handleSignOut();
      } else {
        setDeleteAccountError(
          data.error || t('delete_account_error', 'Failed to delete account. Please try again.'),
        );
      }
    } catch (err) {
      console.error('deleteAccount error:', err);
      setDeleteAccountError(
        t('delete_account_error', 'Failed to delete account. Please try again.'),
      );
    } finally {
      setIsDeletingAccount(false);
    }
  }, [t, handleSignOut]);

  const [fontMode, setFontMode] = useState<string>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('fontMode') || 'torah' : 'torah',
  );
  const [textMode, setTextMode] = useState<string>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('textMode') || 'hebrew' : 'hebrew',
  );
  const [showTransliteration, setShowTransliteration] = useState<boolean>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('showTransliteration') !== 'false' : true,
  );

  const updateFontMode = (mode: 'torah' | 'regular') => {
    trackSelection('font_mode', mode, { page: 'Settings' });
    setFontMode(mode);
    try {
      localStorage.setItem('fontMode', mode);
    } catch {
      /* ignore */
    }
  };

  const updateTextMode = (mode: 'hebrew' | 'translit') => {
    trackSelection('text_mode', mode, { page: 'Settings' });
    setTextMode(mode);
    try {
      localStorage.setItem('textMode', mode);
    } catch {
      /* ignore */
    }
  };

  const updateShowTransliteration = (val: boolean) => {
    trackSelection('show_transliteration', val, { page: 'Settings' });
    setShowTransliteration(val);
    try {
      localStorage.setItem('showTransliteration', val ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="flex flex-col flex-grow"
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <header
        className="px-4 pt-4 pb-5 relative"
        style={{ background: '#FFFFFF', borderBottom: `1px solid ${BORDER_LIGHT}` }}
      >
        <h1 className="text-lg font-semibold text-center" style={{ color: TEXT_DARK }}>
          {t('settings_title')}
        </h1>
      </header>

      <main className="flex-1 px-4 pt-4 space-y-4 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+90px)]">
        {showDeleteAccountConfirm ? (
          <div className="pt-2">
            <button
              onClick={() => {
                setShowDeleteAccountConfirm(false);
                setDeleteAccountError(null);
              }}
              className="flex items-center gap-2 mb-4 font-semibold text-sm"
              style={{ color: BLUE_ACCENT }}
            >
              <span className="text-lg">{isRtl ? '→' : '←'}</span>
              {t('menu_back', 'Back')}
            </button>
            <h3 className="text-lg font-bold mb-2" style={{ color: TEXT_DARK }}>
              {t('delete_account_confirm_title', 'Delete Account?')}
            </h3>
            <p className="text-sm mb-5 leading-relaxed" style={{ color: TEXT_SECONDARY }}>
              {t(
                'delete_account_confirm_message',
                'This will permanently mark your account as deleted. You will be signed out. This action cannot be undone.',
              )}
            </p>
            {deleteAccountError && (
              <p className="text-[13px] mb-3" style={{ color: '#ef4444' }}>
                {deleteAccountError}
              </p>
            )}
            <div className="flex flex-row-reverse gap-3">
              <button
                type="button"
                onClick={() => void handleDeleteAccountConfirm()}
                disabled={isDeletingAccount}
                className="px-5 py-3 rounded-xl text-[15px] font-semibold text-white"
                style={{
                  background: '#ef4444',
                  cursor: isDeletingAccount ? 'not-allowed' : 'pointer',
                  opacity: isDeletingAccount ? 0.7 : 1,
                }}
              >
                {isDeletingAccount
                  ? t('delete_account_confirm_deleting', 'Deleting...')
                  : t('delete_account_confirm_confirm', 'Delete Account')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteAccountConfirm(false);
                  setDeleteAccountError(null);
                }}
                disabled={isDeletingAccount}
                className="px-5 py-3 rounded-xl text-[15px] font-semibold border"
                style={{
                  color: TEXT_DARK,
                  borderColor: BORDER_LIGHT,
                  cursor: isDeletingAccount ? 'not-allowed' : 'pointer',
                }}
              >
                {t('delete_account_confirm_cancel', 'Cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 text-[18px]">
            {/* Font Mode */}
            <div className="flex items-center justify-between">
              <span className="font-semibold" style={{ color: TEXT_DARK }}>
                {t('font_mode')}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => updateFontMode('torah')}
                  className="px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors"
                  style={{
                    background: fontMode === 'torah' ? BLUE_ACCENT : 'white',
                    color: fontMode === 'torah' ? 'white' : TEXT_DARK,
                    borderColor: fontMode === 'torah' ? BLUE_ACCENT : BORDER_LIGHT,
                  }}
                >
                  {t('torah_text')}
                </button>
                <button
                  onClick={() => updateFontMode('regular')}
                  className="px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors"
                  style={{
                    background: fontMode === 'regular' ? BLUE_ACCENT : 'white',
                    color: fontMode === 'regular' ? 'white' : TEXT_DARK,
                    borderColor: fontMode === 'regular' ? BLUE_ACCENT : BORDER_LIGHT,
                  }}
                >
                  {t('regular_text')}
                </button>
              </div>
            </div>
            <p className="text-[10px]" style={{ color: TEXT_SECONDARY }}>
              {fontMode === 'torah' ? t('font_mode_torah_desc') : t('font_mode_regular_desc')}
            </p>

            {/* Text Display Mode */}
            <div className="flex items-center justify-between mt-2">
              <span className="font-semibold" style={{ color: TEXT_DARK }}>
                {t('text_mode')}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => updateTextMode('hebrew')}
                  className="px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors"
                  style={{
                    background: textMode === 'hebrew' ? BLUE_ACCENT : 'white',
                    color: textMode === 'hebrew' ? 'white' : TEXT_DARK,
                    borderColor: textMode === 'hebrew' ? BLUE_ACCENT : BORDER_LIGHT,
                  }}
                >
                  {t('hebrew_text')}
                </button>
                <button
                  onClick={() => updateTextMode('translit')}
                  className="px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors"
                  style={{
                    background: textMode === 'translit' ? BLUE_ACCENT : 'white',
                    color: textMode === 'translit' ? 'white' : TEXT_DARK,
                    borderColor: textMode === 'translit' ? BLUE_ACCENT : BORDER_LIGHT,
                  }}
                >
                  {t('transliteration_text')}
                </button>
              </div>
            </div>
            <p className="text-[10px]" style={{ color: TEXT_SECONDARY }}>
              {textMode === 'hebrew' ? t('text_mode_hebrew_desc') : t('text_mode_translit_desc')}
            </p>

            {/* Show Transliteration */}
            <div className="flex items-center justify-between mt-4">
              <span className="font-semibold" style={{ color: TEXT_DARK }}>
                {t('show_transliteration_label')}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => updateShowTransliteration(true)}
                  className="px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors"
                  style={{
                    background: showTransliteration ? BLUE_ACCENT : 'white',
                    color: showTransliteration ? 'white' : TEXT_DARK,
                    borderColor: showTransliteration ? BLUE_ACCENT : BORDER_LIGHT,
                  }}
                >
                  {t('enable')}
                </button>
                <button
                  onClick={() => updateShowTransliteration(false)}
                  className="px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors"
                  style={{
                    background: !showTransliteration ? BLUE_ACCENT : 'white',
                    color: !showTransliteration ? 'white' : TEXT_DARK,
                    borderColor: !showTransliteration ? BLUE_ACCENT : BORDER_LIGHT,
                  }}
                >
                  {t('disable')}
                </button>
              </div>
            </div>
            <p className="text-[10px]" style={{ color: TEXT_SECONDARY }}>
              {showTransliteration
                ? t('show_transliteration_on_desc')
                : t('show_transliteration_off_desc')}
            </p>

            {/* Delete Account */}
            <button
              onClick={handleDeleteAccountClick}
              className="underline text-[13px] font-medium text-right mt-8 block w-full"
              style={{ color: '#ef4444' }}
            >
              {t('menu_delete_account', 'Delete Account')}
            </button>
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

export default SettingsPage;
